<template>
  <div class="__ProgressBarChart">
    <slot name="head-content" />

    <div :id="`${name}-div`" :style="{ width: '100%', height: height }" />

    <slot name="foot-content" />
  </div>
</template>

<script>
import Vue from 'vue';
import { sleeper } from '@/lib/tools';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import chartColors from '@/components/CustomCharts/colors';
import * as chartFn from '@/components/CustomCharts/chartFn';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

const messages = {
  en: {},
  ja: {},
};

export default Vue.extend({
  name: 'ProgressBar',
  i18n: { messages },
  components: {},
  props: {
    name: {
      type: String,
      default: 'chart',
    },

    data: {
      type: Array,
      required: true,
    },

    series: {
      type: Array,
      required: true,
    },

    // style
    height: {
      type: String,
      default: '40px',
    },
  },
  data() {
    return {
      chart: null,
    };
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },

  methods: {
    setData() {
      this.chart.data = this.data;

      this.chart.series.each(series => {
        const value = this.chart.data[0].amount;
        if (series.id === 'amount') {
          series.fill = am4core.color(chartColors[this.columnColor(value)]);
        }
      });
    },

    async generateChart() {
      await sleeper(500);

      chartFn.initialize();

      am4core.useTheme(am4themes_animated);

      this.chart = am4core.create(`${this.name}-div`, am4charts.XYChart);

      // Add data
      this.chart.data = [];
      this.chart.padding(0, 0, 0, 0);

      // Create axes
      const categoryAxis = this.chart.yAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = 'category';
      categoryAxis.renderer.grid.template.opacity = 0;
      categoryAxis.renderer.labels.template.disabled = true;

      const valueAxis = this.chart.xAxes.push(new am4charts.ValueAxis());
      valueAxis.min = 0;
      valueAxis.renderer.grid.template.opacity = 0;
      valueAxis.renderer.ticks.template.strokeOpacity = 0.5;
      valueAxis.renderer.ticks.template.stroke = am4core.color('#495C43');
      valueAxis.renderer.baseGrid.disabled = true;
      valueAxis.renderer.labels.template.disabled = true;
      valueAxis.renderer.minWidth = 100;
      valueAxis.renderer.minGridDistance = 40;

      // Create series
      this.series.forEach(s => {
        const series = this.chart.series.push(new am4charts.ColumnSeries());
        series.name = s.name;
        series.id = s.id;
        series.dataFields.valueX = s.id;
        series.dataFields.categoryY = 'category';

        series.stacked = true;
        series.columns.template.strokeOpacity = 0;

        if (s.id === 'amount') {
          // Fill color changed by setData method
          series.fillOpacity = 0.9;

          const labelBullet = series.bullets.push(new am4charts.LabelBullet());
          labelBullet.locationX = 0.5;
          labelBullet.label.text = '{valueX}%';
          labelBullet.label.fill = am4core.color('#fff');

          // Tooltip
          series.columns.template.tooltipText = '{valueX}%';
        }

        if (s.id === 'remaining') {
          series.fill = am4core.color(chartColors.grey);
          series.fillOpacity = 0.4;
        }
      });
    },

    columnColor(val) {
      if (val <= 75) return 'green';
      if (val <= 90) return 'orange';
      if (val > 90) return 'red';
      else return 'gray';
    },
  },
});
</script>

<style lang="scss" scoped></style>
